import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';
import {
  messagingService,
  Conversation,
  Message,
  ConversationType,
} from '../services/messagingService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

interface MessagingContextType {
  // State
  conversations: Conversation[];
  currentConversation: Conversation | null;
  messages: Message[];
  unreadCount: number;
  isConnected: boolean;
  isLoading: boolean;
  isLoadingMessages: boolean;
  
  // Actions
  loadConversations: () => Promise<void>;
  loadConversation: (conversationId: string) => Promise<void>;
  clearConversation: () => void;
  sendMessage: (content: string) => Promise<void>;
  createConversation: (
    type: ConversationType,
    participantId?: string,
    contactRequestId?: string,
    initialMessage?: string
  ) => Promise<Conversation>;
  markAsRead: (conversationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  setTyping: (isTyping: boolean) => void;
  
  // Helpers
  getConversationDisplayName: (conversation: Conversation) => string;
}

const MessagingContext = createContext<MessagingContextType | undefined>(undefined);

export function MessagingProvider({ children }: { children: React.ReactNode }) {
  const { user, token } = useAuth();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  
  const currentConversationRef = useRef<string | null>(null);
  const conversationCache = useRef<Map<string, { conversation: Conversation, messages: Message[], timestamp: number }>>(new Map());

  // Initialize socket connection
  useEffect(() => {
    if (!user || !token) {
      if (socket) {
        socket.disconnect();
        setSocket(null);
        setIsConnected(false);
      }
      return;
    }

    const newSocket = io(`${BACKEND_URL}/messaging`, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    newSocket.on('connect', () => {
      console.log('[Messaging] Connected to WebSocket');
      setIsConnected(true);
    });

    newSocket.on('disconnect', () => {
      console.log('[Messaging] Disconnected from WebSocket');
      setIsConnected(false);
    });

    newSocket.on('connect_error', (error) => {
      console.error('[Messaging] Connection error:', error);
      setIsConnected(false);
    });

    // Handle incoming messages
    newSocket.on('new_message', (message: Message) => {
      console.log('[Messaging] New message received:', message);
      
      // Add message to current conversation if it matches
      if (message.conversationId === currentConversationRef.current) {
        setMessages((prev) => {
          const updated = [...prev, message];
          // Update cache with new message
          const cached = conversationCache.current.get(message.conversationId);
          if (cached) {
            conversationCache.current.set(message.conversationId, {
              ...cached,
              messages: updated,
              timestamp: Date.now(),
            });
          }
          return updated;
        });
      } else {
        // Invalidate cache for this conversation (new message received but not currently viewing)
        conversationCache.current.delete(message.conversationId);
      }
      
      // Update conversation in list
      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === message.conversationId
            ? {
                ...conv,
                lastMessage: message,
                lastMessageAt: message.createdAt,
                unreadCount:
                  message.senderId !== user?.id
                    ? conv.unreadCount + 1
                    : conv.unreadCount,
              }
            : conv
        )
      );
    });

    // Handle message notifications (for conversations not currently open)
    newSocket.on('message_notification', (data: { conversationId: string; message: Message }) => {
      console.log('[Messaging] Message notification:', data);
      
      // Update conversation in list if it exists
      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === data.conversationId
            ? {
                ...conv,
                lastMessage: data.message,
                lastMessageAt: data.message.createdAt,
                unreadCount: conv.unreadCount + 1,
              }
            : conv
        )
      );
    });

    // Handle unread count updates
    newSocket.on('unread_count', (data: { unreadCount: number }) => {
      setUnreadCount(data.unreadCount);
    });

    // Handle read receipts
    newSocket.on('messages_read', (data: { conversationId: string; readBy: string }) => {
      if (data.conversationId === currentConversationRef.current) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.senderId === user?.id && !msg.readAt
              ? { ...msg, readAt: new Date().toISOString() }
              : msg
          )
        );
      }
    });

    // Handle typing indicators
    newSocket.on('user_typing', (data: { conversationId: string; userId: string; isTyping: boolean }) => {
      // Could be used to show typing indicator
      console.log('[Messaging] User typing:', data);
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, [user, token]);

  // Load conversations
  const loadConversations = useCallback(async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      const data = await messagingService.getConversations();
      setConversations(data.conversations);
    } catch (error) {
      console.error('[Messaging] Error loading conversations:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Load a specific conversation
  const loadConversation = useCallback(async (conversationId: string) => {
    if (!user || !socket) return;
    
    // Check cache first (cache valid for 30 seconds)
    const cached = conversationCache.current.get(conversationId);
    const cacheAge = cached ? Date.now() - cached.timestamp : Infinity;
    const CACHE_TTL = 30000; // 30 seconds
    
    // Find conversation in list for immediate display
    const conversationFromList = conversations.find(conv => conv.id === conversationId);
    
    // OPTIMIZATION 1: Display conversation immediately from list
    if (conversationFromList) {
      // Convert Conversation to the format expected by currentConversation
      const immediateConversation = {
        id: conversationFromList.id,
        type: conversationFromList.type,
        contactRequestId: conversationFromList.contactRequestId,
        otherParticipant: conversationFromList.otherParticipant,
        createdAt: conversationFromList.createdAt,
      };
      setCurrentConversation(immediateConversation as any);
    }
    
    // OPTIMIZATION 4: Use cache if valid
    if (cached && cacheAge < CACHE_TTL) {
      // Use cached conversation data if available
      if (cached.conversation) {
        setCurrentConversation(cached.conversation);
      }
      setMessages(cached.messages);
      setIsLoadingMessages(false);
      
      // Still update in background for fresh data (but don't show loading spinner)
      // We'll update messages silently when fresh data arrives
    } else {
      // Clear messages and show loading
      setMessages([]);
      setIsLoadingMessages(true);
    }
    
    // Leave previous conversation room
    if (currentConversationRef.current && currentConversationRef.current !== conversationId) {
      socket.emit('leave_conversation', { conversationId: currentConversationRef.current });
    }
    
    // Join new conversation room
    socket.emit('join_conversation', { conversationId });
    currentConversationRef.current = conversationId;
    
    try {
      // OPTIMIZATION 2: Parallelize API calls
      const [conversationData, unreadData] = await Promise.all([
        messagingService.getConversation(conversationId),
        messagingService.getUnreadCount().catch(() => ({ unreadCount: unreadCount })), // Fallback to current count on error
      ]);
      
      // Update conversation with full data
      setCurrentConversation(conversationData.conversation as any);
      setMessages(conversationData.messages);
      
      // OPTIMIZATION 4: Update cache
      conversationCache.current.set(conversationId, {
        conversation: conversationData.conversation as any,
        messages: conversationData.messages,
        timestamp: Date.now(),
      });
      
      // Update unread count in conversations list
      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === conversationId ? { ...conv, unreadCount: 0 } : conv
        )
      );
      
      // Update unread count
      setUnreadCount(unreadData.unreadCount);
    } catch (error) {
      console.error('[Messaging] Error loading conversation:', error);
      // On error, keep the conversation from list displayed
      // If we had cache, keep showing it
      if (!cached || cacheAge >= CACHE_TTL) {
        setIsLoadingMessages(false);
      }
    } finally {
      // Only set loading to false if we weren't using cache
      if (!cached || cacheAge >= CACHE_TTL) {
        setIsLoadingMessages(false);
      }
    }
  }, [user, socket, conversations, unreadCount]);

  // Clear current conversation
  const clearConversation = useCallback(() => {
    if (socket && currentConversationRef.current) {
      socket.emit('leave_conversation', { conversationId: currentConversationRef.current });
    }
    setCurrentConversation(null);
    setMessages([]);
    setIsLoadingMessages(false);
    currentConversationRef.current = null;
  }, [socket]);

  // Send a message
  const sendMessage = useCallback(async (content: string) => {
    if (!currentConversationRef.current || !socket) return;
    
    try {
      socket.emit('send_message', {
        conversationId: currentConversationRef.current,
        content,
      });
      
      // Invalidate cache when sending a message (new message will be added)
      conversationCache.current.delete(currentConversationRef.current);
    } catch (error) {
      console.error('[Messaging] Error sending message:', error);
      throw error;
    }
  }, [socket]);

  // Create a new conversation
  const createConversation = useCallback(async (
    type: ConversationType,
    participantId?: string,
    contactRequestId?: string,
    initialMessage?: string
  ): Promise<Conversation> => {
    const conversation = await messagingService.createConversation({
      type,
      participantId,
      contactRequestId,
      initialMessage,
    });
    
    // Add to conversations list
    setConversations((prev) => {
      const exists = prev.find((c) => c.id === conversation.id);
      if (exists) return prev;
      return [conversation, ...prev];
    });
    
    return conversation;
  }, []);

  // Mark messages as read
  const markAsRead = useCallback(async (conversationId: string) => {
    if (!socket) return;
    
    try {
      socket.emit('mark_read', { conversationId });
      
      // Update local state
      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === conversationId ? { ...conv, unreadCount: 0 } : conv
        )
      );
    } catch (error) {
      console.error('[Messaging] Error marking as read:', error);
    }
  }, [socket]);

  // Mark all conversations as read
  const markAllAsRead = useCallback(async () => {
    if (!socket || !user) return;
    
    try {
      // Get all conversations with unread messages
      const conversationsWithUnread = conversations.filter(conv => conv.unreadCount > 0);
      
      // Mark each conversation as read
      const markPromises = conversationsWithUnread.map(conv => {
        socket.emit('mark_read', { conversationId: conv.id });
        return messagingService.markAsRead(conv.id).catch(err => {
          console.error(`[Messaging] Error marking conversation ${conv.id} as read:`, err);
        });
      });
      
      await Promise.all(markPromises);
      
      // Update local state - set all unread counts to 0
      setConversations((prev) =>
        prev.map((conv) => ({ ...conv, unreadCount: 0 }))
      );
      
      // Update unread count to 0
      setUnreadCount(0);
      
      // Refresh unread count from server to ensure consistency
      messagingService.getUnreadCount()
        .then((data) => setUnreadCount(data.unreadCount))
        .catch(console.error);
    } catch (error) {
      console.error('[Messaging] Error marking all as read:', error);
    }
  }, [socket, user, conversations]);

  // Set typing status
  const setTyping = useCallback((isTyping: boolean) => {
    if (!socket || !currentConversationRef.current) return;
    
    socket.emit('typing', {
      conversationId: currentConversationRef.current,
      isTyping,
    });
  }, [socket]);

  // Helper to get display name for a conversation
  const getConversationDisplayName = useCallback((conversation: Conversation): string => {
    const { otherParticipant } = conversation;
    return `${otherParticipant.firstName} ${otherParticipant.lastName}`;
  }, []);

  // Load initial unread count
  useEffect(() => {
    if (user) {
      messagingService.getUnreadCount()
        .then((data) => setUnreadCount(data.unreadCount))
        .catch(console.error);
    }
  }, [user]);

  const value: MessagingContextType = {
    conversations,
    currentConversation,
    messages,
    unreadCount,
    isConnected,
    isLoading,
    isLoadingMessages,
    loadConversations,
    loadConversation,
    clearConversation,
    sendMessage,
    createConversation,
    markAsRead,
    markAllAsRead,
    setTyping,
    getConversationDisplayName,
  };

  return (
    <MessagingContext.Provider value={value}>
      {children}
    </MessagingContext.Provider>
  );
}

export function useMessaging() {
  const context = useContext(MessagingContext);
  if (context === undefined) {
    throw new Error('useMessaging must be used within a MessagingProvider');
  }
  return context;
}

export default MessagingContext;

